import * as React from 'react';
import { History } from 'history';
import { Checkbox, Grid } from '@mui/material';
import { ActionButton, Button, Card, Chip, Gallery, Icon, InputBulkUpload, InputRating, InputSearch, Media, Pagination, ProgressBar, Spinner, Text, ThemeTextSize } from '.';
import AppService, { align, direction, size } from '../services/app.service';

interface Props {
  id?: string;
  history?: History;
  header: any;
  dataset: Array<any> | any;
  currentId?: any,
  replaceText?: any,
  nodataCaption?: string;
  page?:number;
  pageCount?: number;
  maxHeight?: number; 
  actions?: any;
  searchText?: string;
  isSearch?: boolean;
  isValidateError?: boolean;
  isLoading?: boolean;
  isReady?: boolean;
  isError?: boolean;
  onLoad?: (data: any) => void;
  onChangePage?: (value: number) => void;
  onChangeSearch?: (fieldName: string, value?: string) => void;
  onChangeSwipe?: (value: string) => void;
  onClickSearch?: () => void;
  onClickUpload?: () => void;
  onClickCreate?: (data: any) => void;
  onEndTypingSearch?: (fieldName?: string, value?: string) => void;
  cardColor?: any;
  cardDirection?: direction;
  cardAlign?: align;
  cardCount?: number;
  cardBorderWidth?: number;
  cardBorderRadius?: number;
  cardBoxShadow?: number;
  cardPadding?: number;
  cardSpacing?: number;
  cardIconOnly?: boolean;
  cardRightArrow?: boolean;
  cardHeader?: boolean;
  cardCollapse?: boolean;
  swipe?: string;
  paginationAlign?: align; 
  paginationSize?: size;
  paginationPrevCaption?: string;
  paginationNextCaption?: string;
  autoSelect?: boolean; //when reload auto select base od last selected id
  autoUnselect?: boolean; //auto unselect after click card
  checklistView?: boolean;
  cardView?: boolean;
  readOnly?: boolean;
  disablePagination?: boolean;
  disablePaginationLabel?: boolean;
  disablePaginationButtonPage?: boolean;
  disablePaginationButtonPrev?: boolean;
  disablePaginationButtonNext?: boolean;
  children?: React.ReactNode;
  
}

export const Table: React.FunctionComponent<Props> = (props) => {
  const isMobile = AppService.isMobile();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [selectedId, setSelectedId] = React.useState<any>(props.currentId);
  const [hoverId, setHoverId] = React.useState<any>(-1);
  const [checkAll, setCheckAll] = React.useState(false);
  const [isUpload, setIsUpload] = React.useState(true);
  const [cardWidth, setCardWidth] = React.useState(0);
  const [collapse, setCollapse] = React.useState(props.cardCollapse);

  const findId = (id: number) => {
    return selectedId?.findIndex((d: any) => d.id === id) >= 0;
  }

  const findAction = (action: string) => {
   return props.actions?.filter((d: any) => d.id === action)?.[0];
  }

  const onClick = (data: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
    if (props.readOnly) return;
    props.onLoad?.(data);
    if (!props.actions || props.actions.length>1) setAnchorEl(e.currentTarget);
  }
  
  const onClickCard = (data: any) => {
    if (!props.readOnly)
    if (props.checklistView) {
      const selected = findId(data.id)? selectedId.filter((d: any) => d.id !== data.id) : [...selectedId, data];
      setSelectedId(selected);
      props.onLoad?.(selected);
    }
    else {
      if (!props.autoUnselect) setSelectedId(data.id);
      props.onLoad?.(data);
    }
  }

  const onClickCheckAll = () => {
    const selected = checkAll? [] : props.dataset;
    setSelectedId(selected);
    props.onLoad?.(selected);
  }
  
  const onClickUpload = (e: React.MouseEvent<HTMLButtonElement>) => {
    props.onClickUpload?.();
    setIsUpload(false);
  }

  const onLoadUpload = (data: any) => {
    setIsUpload(true);
    props.onLoad?.(data)
  }

  const getCardWidth = (value: number) => {
    if (value > 0) setCardWidth(value);
  }

  const tableHeader = () => {
    if (props.header?.filter((d: any) => d.caption.trim()).length === 0 ) return;
    let checkBoxAll =  props.checklistView && !props.readOnly && props.dataset.length > 0 && <Checkbox sx={{'& .MuiSvgIcon-root': {fontSize: ThemeTextSize().iconSize, color: 'white'}, p: 0, m: -0.5, pr: 2.5}} checked={checkAll} onChange={onClickCheckAll}/>

    return (
      <Grid container gridTemplateColumns={'2fr 2fr' + ' 1fr'.repeat(Math.max(props.header.length - 2, 0))} display='grid' sx={{borderRadius: 20, backgroundColor: 'primary.light', p: 1, mb: 1}}>          
        {props.header.map((d: any, i: number) => {
          if (d.caption){
            switch (d.type) {
              case 'number':
              case 'percentage':       
              case 'money': return <Grid key={i} item textAlign='right' gridColumn={'span ' + d.span?.toString()} pr={1}> <b>{d.caption}</b></Grid>
              case 'state':
              case 'rating':
              case 'validate': return <Grid key={i} textAlign='center' gridColumn={'span ' + d.span?.toString()}> <b>{d.caption}</b> </Grid>
              default : return <Grid key={i} display='flex' gridColumn={'span ' + d.span?.toString()}> {i === 0 && <>{checkBoxAll}</>}<b>{d.caption}</b> </Grid>
            }
          }
        })}
      </Grid>
    )
  }

  const tableContent = () => {
    if (!props.isReady && !props.dataset[0])
      return <Grid container justifyContent='center' sx={{colSpan: props.header.length + 1}}><Spinner/></Grid>
    
    if (props.isError) 
      return <Grid container justifyContent='center' sx={{colSpan: props.header.length + 1}}><Chip label='Sorry.. Unable to load data, there is something error.' type='error'/></Grid>

    if (!props.dataset || props.dataset?.length===0) 
      return <Grid container justifyContent='center' sx={{colSpan: props.header.length + 1}}><Chip label={props.nodataCaption ||'no options'} /></Grid>
    
    return props.dataset.map((d: any, i: number) => {
      let checkBox = props.checklistView && <Checkbox id={i.toString()} sx={{padding: isMobile? 0.6 : 1, '& .MuiSvgIcon-root': {fontSize: ThemeTextSize().iconSize}, margin: -0.2}} checked={findId(d.id)} onChange={() => onClickCard(d)}/>
          
      return (
        <Grid key={i} container alignItems='center' gridTemplateColumns= {'2fr 2fr' + ' 1fr'.repeat(Math.max(props.header.length - 2, 0))} display='grid' sx={{borderBottom: 1, borderColor: 'background.paper', color: 'text.secondary', verticalAlign: 'middle', backgroundColor: d.id > 0? (selectedId === d.id? 'background.default' : hoverId === d.id? 'background.paper' : '') : '', p: isMobile? 1 : 0.2, }} onMouseEnter={() => setHoverId(d.id)} onMouseLeave={() => setHoverId(0)} >
          {props.header.map((d1: any, j: number) => {
            if (typeof d[d1.name] === 'string' && props.replaceText)
              d[d1.name] = AppService.replaceValue(d[d1.name], props.replaceText);
            let isTrue = d[d1.name] === d1.default;
            if (j===0)
              return <Grid key={j} display='flex'>{checkBox}<Button id={d?.id} label={d[d1.name]} textTransform='capitalize' fullWidth sx={{textAlign: 'left', padding: 0.5, paddingLeft: 1, paddingRight: 1, borderRadius: 1}} onClick={onClick(d)} /></Grid>
            else
              switch (d1.type) {
                case 'state': return <Grid key={j} textAlign='center'> <Chip label={AppService.strCapitalize(d[d1.name])} type={isTrue? 'primary' : 'default'} /> </Grid>                  
                case 'number': return <Grid key={j} textAlign='right' pr={1}> {d[d1.name]}</Grid> 
                case 'money': return <Grid key={j} textAlign='right' pr={2}> {AppService.strToMoney(d[d1.name])}</Grid> 
                case 'percentage': return <Grid key={j} textAlign='right' pr={1}> {d[d1.name]? d[d1.name]+ '%' : ''}</Grid> 
                case 'date': return <Grid key={j}> {AppService.dateFormat(d[d1.name],'d mon y')}</Grid> 
                case 'time': return <Grid key={j}> {AppService.dateFormat(d[d1.name],'t')}</Grid> 
                case 'time2': return <Grid key={j}> {AppService.dateFormat(d[d1.name],'h:n')}</Grid> 
                case 'datetime': return <Grid key={j}> {AppService.dateFormat(d[d1.name],'d mon y, h:n')}</Grid> 
                case 'rating': return <Grid key={j} textAlign='center'> <InputRating name={d1.name} value={d[d1.name]} size='extrasmall' readOnly disableMargin/> </Grid>
                case 'validate': return <Grid key={j} textAlign='center'> <Text label={d[d1.name]?.data || d1.default} color={d[d1.name]?.error && 'red'} size={d[d1.name]?.error? 'extrasmall' : 'small'} font={d1.font} textToHtml icon={d[d1.name]?.error? 'error' : 'check_circle'} iconSize='medium'/> </Grid>
                default : return <Grid key={j}> <Text label={d[d1.name]?.length > 100? d[d1.name].substring(0,100) + ' ...': d[d1.name]} font={d1.font} textToHtml/></Grid>
              }
          })}
        </Grid>
      )
    });

  }

  const tableContentCardView = () => {
    if (!props.isReady && !props.dataset[0])
      return <Spinner/>

    if (props.isError) 
      return <Chip label='Sorry.. Unable to load data, there is something error.' type='error'/>

    if (!props.dataset || props.dataset?.length===0)
      return  <Grid key={0} item xs={12}> <Chip label={props.nodataCaption ||'no options'}/> </Grid>
    
    return props.dataset.map((d: any, i: number) => {
      let cardHeader: any = null;
      let popOver: string = '';
      let cardMedia: React.ReactNode = '';
      let cardIcon: React.ReactNode = '';
      let cardProgress: React.ReactNode = '';
      let cardContent: React.ReactNode = '';
      let cardOption: any = null;
      let cardSelected = props.checklistView? findId(d) : ((!selectedId) && props.autoSelect && (i ===  0)) || (selectedId === d.id);
      let noCaption = props.header.filter((d1: any) => d1.type === 'caption').length === 0;
      let noPointer = props.header.filter((d1: any) => ['button','children'].includes(d1.type)).length > 0;

      props.header.map((d1: any, j: number) => {
        if (typeof d[d1.name] === 'string' && props.replaceText)
          d[d1.name] = AppService.replaceValue(d[d1.name], props.replaceText);
        let isTrue = d[d1.name] === d1.default;
        let align = d1.textAlign || props.cardAlign;
        
        if (d[d1.name] || (!d[d1.name] && !d1.hideNull) || noPointer)
        if (d1.type === 'option')
          cardOption = d[d1.name]
        else if (d1.type === 'state')
          cardContent = <>{cardContent} <Chip key={j} label={AppService.strCapitalize(d[d1.name])} type={isTrue? 'primary' : 'default'} sx={{marginTop: 1, marginBottom: 1}} /> </>            
        else if (d1.type === 'caption') {
          if (props.cardHeader && props.swipe !== 'horizontal')
            cardHeader = {...d1, caption: d[d1.name], textAlign: align}
          else 
            cardContent = <>{cardContent} <Grid container direction={align === 'center'? 'column':'row'} alignItems='center'>{cardIcon}<Grid item xs md><Text key={j} label={d[d1.name]} color={d1.color} size={d1.size} font={d1.font} lineHeight={d1.lineHeight || 1.2} textAlign={d[d1.name].length===1? 'center' : align} bold marginTop={d1.marginTop || (cardContent? 1 : 0)} /></Grid></Grid></>;
          popOver = props.cardIconOnly? d[d1.name] : undefined;
        }
        else if (d1.type === 'number')
          cardContent = <>{cardContent} <Text key={j} label={d[d1.name]} size='small' color='text.secondary' font={d1.font} marginTop={cardContent? 1 : 0} /></>                
        else if (d1.type === 'money')
          cardContent = <>{cardContent} <Text key={j} label={AppService.currencySymbol + ' ' + AppService.strToMoney(d[d1.name])} color='text.secondary' font={d1.font} bold marginTop={cardContent? 1 : 0} /></>                
        else if (d1.type === 'percentage')
          cardContent = <>{cardContent} <Text key={j} label={d[d1.name]? d[d1.name]+ '%' : ''} size='small' color='text.secondary' font={d1.font} marginTop={d1.marginTop || (cardContent? 1 : 0)} /></>                
        else if (d1.type === 'date')
          cardContent = <>{cardContent} <Text key={j} label={AppService.dateFormat(d[d1.name],'d mon y')} size='small' color='text.secondary' font={d1.font} icon='calendar_month' displayBlock marginTop={d1.marginTop || (cardContent? 1 : 0)} /></>                
        else if (d1.type === 'time')
          cardContent = <>{cardContent} <Text key={j} label={AppService.dateFormat(d[d1.name],'t')} size='small' color='text.secondary' font={d1.font} icon='schedule' displayBlock marginTop={d1.marginTop || (cardContent? 1 : 0)} /></>                
        else if (d1.type === 'time2')
          cardContent = <>{cardContent} <Text key={j} label={AppService.dateFormat(d[d1.name],'h:n')} size='small' color='text.secondary' font={d1.font} icon='schedule' displayBlock marginTop={d1.marginTop || (cardContent? 1 : 0)} /></>                
        else if (d1.type === 'datetime')
          cardContent = <>{cardContent} <Text key={j} label={AppService.dateFormat(d[d1.name],'d mon y, h:n')} size='small' color='text.secondary' font={d1.font} icon='calendar_month' iconSize='small' displayBlock marginTop={cardContent? 1 : 0} /></>                
        else if (d1.type === 'icon') 
          cardIcon = <Icon name={d[d1.name]} size={d1.size || 'large'} marginRight={!props.cardIconOnly} />              
        else if (d1.type === 'media')
          cardMedia = <Media key={j} url={d[d1.name]} sx={{objectFit: 'cover', ...d1, ...cardOption?.media}}/>
        else if (d1.type === 'rating')
          cardContent = <>{cardContent} <InputRating name={d1.name} value={d[d1.name]} size='extrasmall' readOnly disableMargin/> </>            
        else if (d1.type === 'button')
          cardContent = <>{cardContent} <Button type='contained' label={d1.caption} sx={{mt: 3, mb: 3}} onClick={() => onClickCard(d)} /></>                
        else if (d1.type === 'progress')
          cardProgress = !!d[d1.total] && <ProgressBar value={d[d1.name]} totalValue={d[d1.total]} icon={d1.icon} iconColor={d1.iconColor}/>
        else if (d1.type === 'children')
          cardContent = <>{cardContent} <Grid container justifyContent={align} marginTop={3}>{React.isValidElement(d1.children) && React.cloneElement(d1.children, {[d1.name]: d})}</Grid> </>                
        else
          cardContent = <>{cardContent} <Grid container direction={align === 'center'? 'column':'row'} alignItems='center'>{noCaption && cardIcon}<Grid item xs md><Text key={j} id={d1.name + i} label={d[d1.name]} size={d1.size || 'small'} font={d1.font} lineHeight={d1.lineHeight} color={d1.color || 'text.secondary'} textAlign={align} textToHtml showMore={d1.noWrap? (props.dataset.length > 1? 100 : undefined) : -100} marginTop={d1.marginTop || (cardContent? 1 : 0)} /></Grid></Grid></>
      })
      
      if (cardProgress || props.cardRightArrow)
        cardContent = 
          <Grid container alignItems='center' justifyContent='space-between'>
            <Grid item xs={9} md={10}> {cardContent} </Grid>
            {cardProgress}
            {props.cardRightArrow && <Icon name='navigate_next' />}
          </Grid>
      return(
        <Grid key={i} sx={{cursor: noPointer? '' : 'pointer', pointerEvents: (props.readOnly? 'none' : 'auto')}} onClick={() => !noPointer && onClickCard(d)}> 
          <Card mediaAsBackground={cardOption?.media?.asBackground} media={cardMedia} header={cardHeader} direction={props.cardDirection} selected={cardSelected} popOver={popOver} color={props.cardColor} width={props.cardIconOnly? 'auto' : cardWidth} fullHeight={props.swipe === 'horizontal' || props.cardIconOnly} borderWidth={props.cardBorderWidth} borderRadius={props.cardIconOnly? 50: props.cardBorderRadius} boxShadow={props.cardBoxShadow || (noPointer? 0 : 1)} padding={props.cardIconOnly? 1 : props.cardPadding} collapse={collapse}>
            {props.cardIconOnly? cardIcon : cardContent }
          </Card>
        </Grid>
      )
    });

  }

  const table = () => {
    const actionCreate = findAction('create');
    const actionUpload = findAction('upload');
    
    return (
      <Grid container>
      <Grid container alignItems='end' justifyContent='space-between' mb={1}>
        {!!props.children &&
        <Grid>
          {props.children}
        </Grid>
        }
        {actionCreate &&
        <Grid>
          <Button type='outlined' label='Add New' startIcon='add' onClick={() => {props.onClickCreate?.(actionCreate)}} /> 
        </Grid>
        }
        {actionUpload &&
        <Grid>
          <InputBulkUpload name='upload' buttonCaption='Select File' onLoad={onLoadUpload} disableMargin/>
        </Grid>
        }
        {((props.pageCount||1) > 1 || props.searchText) && props.isSearch &&
        <Grid>
          <InputSearch name='searchText' value={props.searchText} onChange={props.onChangeSearch} onClick={props.onClickSearch} onEndTyping={props.onEndTypingSearch} readOnly={props.isLoading} disableMargin/>
        </Grid>
        }
        {props.cardView && props.cardHeader &&
        <Grid>
          <Button startIcon={collapse? 'keyboard_double_arrow_right' : 'keyboard_double_arrow_down'} onClick={() => setCollapse(!collapse)}/>
        </Grid>
        }
      </Grid>
        
      {props.cardView ?
        <Gallery dataLength={props.dataset?.length} cardCount={props.cardCount} cardBorderWidth={props.cardBorderWidth} cardAlign={props.cardAlign} spacing={props.cardSpacing} page={props.page||1} pageCount={props.pageCount||1}  swipe={props.swipe} getCardWidth={getCardWidth} onChange={props.onChangeSwipe}>
          {tableContentCardView()}
        </Gallery>
      :
      <Grid container>
        {tableHeader()}
        <Grid container maxHeight={props.maxHeight} sx={{overflowY: 'auto', '::-webkit-scrollbar': {display: 'none'}}}>
          {tableContent()}
        </Grid>
      </Grid>
      }
              
      </Grid>
    )
  }

  React.useEffect(() => { 
    setSelectedId(props.currentId);
    if (props.checklistView) setCheckAll(props.dataset.length === (selectedId.length || 0));

  }, [props.page, props.isLoading, props.isReady, props.checklistView, props.dataset, props.currentId, selectedId])

  return (
    <Grid container>
    {table()}
    
    {!props.disablePagination &&
      <Pagination pageCount={props.pageCount} page={props.page} align={props.paginationAlign} size={props.paginationSize} prevCaption={props.paginationPrevCaption} nextCaption={props.paginationNextCaption} disablePageLabel={props.disablePaginationLabel} disableButtonPage={props.disablePaginationButtonPage || props.cardIconOnly} disableButtonPrev={props.disablePaginationButtonPrev} disableButtonNext={props.disablePaginationButtonNext} onChange={props.onChangePage} />
    }
    {findAction('upload') && !props.isValidateError && props.isReady && isUpload &&
      <Button type='contained' label='Upload' startIcon='cloud_upload' sx={{mt: 2, mb: 3}} onClick={onClickUpload} /> 
    }
    {props.actions &&
      <ActionButton history={props.history} anchorEl={anchorEl} action= {props.actions} onClose={() =>setAnchorEl(null)} />      
    }
    </Grid>
  )
}
